import * as React from 'react';
import { isNil } from 'lodash';
import { Button, Tooltip, message, type Table } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import copy from 'copy-to-clipboard';
import { formatter } from '@shared/utils';
import * as Styles from './styled';
import { FULL_STEP_AGGREGATION_REPLACE_MAP } from '@src/shared/constants/analysis';

export interface FunnelDimensionTableProps {
  loading?: boolean;
  data?: PatternQueryResponseItem[];
  events: string[];
  dimension: {
    id: string;
    name: React.ReactNode;
  };
  aggregations: Aggregation[];
  control?: React.ReactNode;
  sort?: { step: 'full' | number; field: string; order: 'asc' | 'desc' };
  onSort?: (sort?: FunnelDimensionTableProps['sort']) => void;
  onDimensionValueClick?: (value: {
    dimension: string;
    value: string | number;
    label: string;
  }) => void;
}

type DataItem = Record<string, string | number | PatternQueryResponseItem<string | number>>;

type ColumnType = GetProp<typeof Table<DataItem>, 'columns'>;

const SORT_ORDERS_MAP = {
  asc: 'ascend',
  desc: 'descend',
} as const;

const FULL_STEP_COLUMN_KEY = 'full';

const FunnelDimensionTable = (props: FunnelDimensionTableProps) => {
  const {
    loading,
    data = [],
    dimension,
    aggregations,
    events,
    sort,
    control,
    onDimensionValueClick,
    onSort,
  } = props;

  const tableDataSource = React.useMemo(() => {
    if (!data?.length) {
      return [];
    }
    const result: Record<string, number | string | PatternQueryResponseItem>[] = [];
    data.reduce(
      (acc, item) => {
        const dimensionValue = item[dimension.id];
        if (!acc[dimensionValue.val]) {
          acc[dimensionValue.val] = {
            [dimension.id]: dimensionValue,
          };
          result.push(acc[dimensionValue.val]);
        }

        acc[dimensionValue.val][item.step] = item;

        return acc;
      },
      {} as Record<string | number, Record<string, number | string | PatternQueryResponseItem>>,
    );

    return result;
  }, [data, dimension.id]);

  const columns = React.useMemo<ColumnType>(() => {
    const baseColumns = [
      {
        title: <Styles.Control>{control}</Styles.Control>,
        children: [
          {
            title: <Styles.GrayHeaderText>{dimension.name}</Styles.GrayHeaderText>,
            dataIndex: dimension.id,
            key: dimension.id,
            render: (value) => {
              const { label, val } = value ?? {};

              if (isNil(val) || val === '') {
                return null;
              }

              const cellDisplay = label ?? val;

              if (val === 'UNKNOWN') {
                return <Styles.GrayHeaderText>{cellDisplay}</Styles.GrayHeaderText>;
              }

              return (
                <Styles.DimensionValueContainer>
                  <Tooltip placement="top" title={cellDisplay}>
                    <Styles.DimensionValue
                      onClick={() =>
                        onDimensionValueClick?.({
                          dimension: dimension.id,
                          value: val,
                          label: cellDisplay,
                        })
                      }
                    >
                      {cellDisplay}
                    </Styles.DimensionValue>
                  </Tooltip>
                  <Button
                    variant="text"
                    color="primary"
                    icon={<CopyOutlined />}
                    onClick={() => {
                      copy(cellDisplay);
                      message.info('Copied to clipboard');
                    }}
                  />
                </Styles.DimensionValueContainer>
              );
            },
            width: 280,
            fixed: 'left' as const,
          },
        ],
        fixed: 'left' as const,
      },
      {
        title: <Styles.HeaderText>Full Step</Styles.HeaderText>,
        children: aggregations.map((aggregation) => {
          const stepKey = `step_${events.length}`;
          const { id: rawId, name, unit } = aggregation;
          const id = FULL_STEP_AGGREGATION_REPLACE_MAP[rawId] ?? rawId;

          return {
            title: <Styles.GrayHeaderText>{name}</Styles.GrayHeaderText>,
            dataIndex: [stepKey, id],
            key: `${FULL_STEP_COLUMN_KEY}.${id}`,
            width: 100,
            render: (_, record) => {
              const v = record[stepKey]?.[id];
              return v ? `${formatter[unit ?? '#']?.(v) ?? v}` : '-';
            },
            sorter: true,
            sortOrder:
              sort?.field === id && sort?.step === FULL_STEP_COLUMN_KEY
                ? SORT_ORDERS_MAP[sort.order]
                : null,
          };
        }),
      },
    ];

    const stepColumns = events.map((event, index) => {
      const stepNum = index + 1;
      const stepKey = `step_${stepNum}`;

      return {
        title: <Styles.HeaderText>{`Step ${stepNum}`}</Styles.HeaderText>,
        children: aggregations.map((aggregation) => {
          const { id, name, unit } = aggregation;

          return {
            title: <Styles.GrayHeaderText>{name}</Styles.GrayHeaderText>,
            dataIndex: [stepKey, id],
            key: `${stepNum}.${id}`,
            width: 100,
            render: (_, record) => {
              const v = record[stepKey]?.[id];
              return v ? `${formatter[unit ?? '#']?.(v) ?? v}` : '-';
            },
            sorter: true,
            sortOrder:
              sort?.field === id && sort?.step === stepNum ? SORT_ORDERS_MAP[sort.order] : null,
          };
        }),
      };
    });

    return [...baseColumns, ...stepColumns.flat()];
  }, [sort, control, dimension, events, aggregations, onDimensionValueClick]);

  const handleChange = React.useCallback<GetProp<typeof Table<DataItem>, 'onChange'>>(
    (pagination, filters, sorter) => {
      if (!sorter || Array.isArray(sorter) || !Array.isArray(sorter.field)) {
        onSort?.();
        return;
      }

      const step = (sorter.columnKey as string)?.startsWith(FULL_STEP_COLUMN_KEY)
        ? FULL_STEP_COLUMN_KEY
        : +sorter.field[0].replace('step_', '');

      if (step !== FULL_STEP_COLUMN_KEY && isNaN(step)) {
        onSort?.();
        return;
      }

      onSort?.({
        step,
        field: sorter.field[1],
        order: sorter.order === 'ascend' ? 'asc' : 'desc',
      });
    },
    [onSort],
  );

  return (
    <Styles.StyledTable
      rowKey={(record) => record[dimension.id]?.val ?? JSON.stringify(record[dimension.id])}
      dataSource={tableDataSource}
      columns={columns}
      pagination={false}
      size="small"
      virtual
      loading={loading}
      bordered={false}
      sortDirections={['ascend', 'descend', 'ascend']}
      scroll={{ y: 300 }}
      onChange={handleChange}
    />
  );
};

export default FunnelDimensionTable;
